let installed = false;

function shouldKeepPerfLogsVisible(): boolean {
  if (typeof window === 'undefined') return false
  const w = window as any
  if (w.__FG_KEEP_PERF_LOGS__ === true) return true
  if (w.__FG_DIAG === true || w.__FG_PERF === true) return true
  try {
    const sp = new URLSearchParams(String(window.location?.search || ''))
    const truthy = (v: any) => ['1', 'true', 'yes', 'on'].includes(String(v ?? '').trim().toLowerCase())
    if (truthy(sp.get('perf'))) return true
    if (truthy(sp.get('fg_debug_crosstab_scroll'))) return true
    if (truthy(sp.get('fg_keep_perf_logs'))) return true
    if (truthy(sp.get('fg_diag_console'))) return true
  } catch { }
  try {
    const truthy = (v: any) => ['1', 'true', 'yes', 'on'].includes(String(v ?? '').trim().toLowerCase())
    if (truthy(window.localStorage?.getItem('fg_debug_crosstab_scroll'))) return true
    if (truthy(window.localStorage?.getItem('fg_keep_perf_logs'))) return true
    if (truthy(window.localStorage?.getItem('fg_diag_console'))) return true
  } catch { }
  return false
}

function isHighVolumeDiagnosticMessage(msg: string): boolean {
  const text = msg.trim()
  if (!text) return false
  if (text.startsWith('[vite] hot updated:')) return true
  return (
    text.startsWith('[FG-PERF') ||
    text.startsWith('[FG-RESTORE-') ||
    text.startsWith('[FG-IMPORT-LINEAGE]') ||
    text.startsWith('[FG-WORKBOOK-SHARED-BASE]') ||
    text.startsWith('[FG-APP-POSTMESSAGE]') ||
    text.startsWith('[FG-ROUTE-TAB]') ||
    text.startsWith('[FG-BUILDER-TRACE]') ||
    text.startsWith('[FG-BUILD-WORKER-STAGES]')
  )
}

function isUnassociatedMotherDuckEvent(args: unknown[]): boolean {
  if (args.length < 1) return false;
  const first = args[0];
  if (typeof first !== 'string') return false;

  const msgLower = first.toLowerCase();
  if (!msgLower.includes('unassociated response')) return false;

  // Case 1: single string arg includes the payload
  if (msgLower.includes('md_event')) return true;

  // Case 2: two-arg form: "unassociated response:" [undefined, 'MD_EVENT']
  const second = args[1];
  if (Array.isArray(second)) {
    return second.length >= 2 && String(second[1]).toUpperCase() === 'MD_EVENT';
  }

  return false;
}

function isDuckDbInternalLogObject(args: unknown[]): boolean {
  if (args.length < 1) return false
  const first = args[0] as any
  if (!first || typeof first !== 'object') return false
  // duckdb-wasm sometimes logs structured objects like:
  // { timestamp, level, origin, topic, event, ... }
  return (
    typeof first.timestamp === 'string' &&
    typeof first.level === 'number' &&
    typeof first.topic === 'number'
  )
}

/**
 * Console focus mode: suppress known-noisy library logs so real errors stand out.
 * Disable by setting `window.__FG_CONSOLE_FOCUS = false` before app boot.
 */
export function installConsoleFocus() {
  if (installed) return;
  if (typeof window === 'undefined') return;
  if ((window as unknown as { __FG_CONSOLE_FOCUS?: unknown }).__FG_CONSOLE_FOCUS === false) return;

  installed = true;
  const keepPerfLogs = shouldKeepPerfLogsVisible()

  const wrap = (method: 'log' | 'warn' | 'info' | 'error' | 'debug') => {
    const original = (console[method] as (...a: unknown[]) => void).bind(console);
    console[method] = (...args: unknown[]) => {
      if (isUnassociatedMotherDuckEvent(args)) return;
      if (isDuckDbInternalLogObject(args)) return;

      // Silence performance logs
      const msg = args[0];
      if (!keepPerfLogs && typeof msg === 'string') {
        if (msg.includes('[rafdrop]')) return;
        if (msg.includes('[longtask]')) return;
        if (msg.includes('[looptick]')) return;
        if (msg.includes('Violation')) return;
        if (msg.includes('[diag]')) return;
        if (method !== 'error' && isHighVolumeDiagnosticMessage(msg)) return;
      }

      original(...args);
    };
  };

  wrap('log');
  wrap('warn');
  wrap('info');
  wrap('error');
  wrap('debug');
}
